import { createMemo, createSignal } from "solid-js";
import { Dynamic } from "solid-js/web";
import EventAnalytics from "~/js/utilities/EventAnalytics";
import { useCustomContext } from "~/solidJs/shared";
import { getNodeBasePrice } from "~/solidJs/shared/helpers/nodeHelpers";
import type { EntitySize } from "~/solidJs/shared/helpers/types/ISizeble";
import { Menu } from "~/solidJs/shared/ui/molecules/Menu";
import { getContextMenuActionsForViewerType, getFooterActionsForViewerType } from "./handlers";
import type { HoverFeatureAction, HoverFeatureMenuContext, HoverFeatureMenuState } from "./model";
import * as HoverFeatureMenuModel from "./model";
import { RenderContextMenuItem } from "./ui";

// Global signal for HoverFeatureMenu state
const [globalState, setGlobalState] = createSignal<HoverFeatureMenuState>({
	isOpen: false,
	isContextMenuOpen: false,
	context: null
});

// Global function to open HoverFeatureMenu from viewer
export const openHoverFeatureMenuFromViewer = (context: HoverFeatureMenuContext) => {
	const current = globalState();
	setGlobalState({
		isOpen: true,
		isContextMenuOpen: current.context?.viewer === context.viewer && current.isContextMenuOpen,
		context
	});
};

export const updateSize = (size: EntitySize) => {
	setGlobalState((prev) => ({
		...prev,
		context: prev.context ? { ...prev.context, size } : null
	}));
};

// Global function to close from viewer (only if menu is not hovered)
export const closeHoverFeatureMenuFromViewer = () => {
	const current = globalState();
	if (current.isContextMenuOpen) {
		return;
	}
	setGlobalState({
		isOpen: false,
		isContextMenuOpen: false,
		context: null
	});
};

export const openContextMenu = () => {
	const cur = globalState();
	if (cur.isContextMenuOpen) return;
	setGlobalState((prev) => ({ ...prev, isContextMenuOpen: true }));
};

export const closeHoverFeatureMenu = () => {
	setGlobalState({
		isOpen: false,
		isContextMenuOpen: false,
		context: null
	});
};

// Functions to get footer and context menu actions
export const getFooterActions = (context: HoverFeatureMenuContext): HoverFeatureAction[] => {
	return getFooterActionsForViewerType(context.viewerType, context);
};

export const getContextMenuActions = (
	context: HoverFeatureMenuContext
): Menu.ClosedMenuTypesUnion[] => {
	return getContextMenuActionsForViewerType(context.viewerType, context);
};

// Function to convert HoverFeatureAction to ClosedMenuItem
export const convertHoverFeatureActionToMenuItem = (
	action: HoverFeatureAction
): Menu.ClosedMenuItem => ({
	id: action.id,
	type: Menu.ClosedMenuItemType.Item,
	label: action.label,
	onSelect: () => {
		EventAnalytics.hoverUsed(action.id, "context_menu");
		action.handler();
	},
	getIsDisabled: action.disabled,
	renderItem: (p) => <RenderContextMenuItem action={action} uiPorps={p as any} />,
	Icon: () => <Dynamic component={action.IconContext} />
});

export const useHoverFeatureMenu = () => {
	const footerActions = createMemo((): HoverFeatureAction[] => {
		const state = globalState();
		if (!state.context) return [];

		return getFooterActions(state.context);
	});

	const contextMenuActions = createMemo((): Menu.ClosedMenuTypesUnion[] => {
		const state = globalState();
		if (!state.context) return [];

		return getContextMenuActions(state.context);
	});

	const getMenuSize = (): HoverFeatureMenuModel.HoverMenuSize => {
		const width = globalState().context?.size.width;
		if (!width) return HoverFeatureMenuModel.HoverMenuSize.Medium;
		if (width < 250) return HoverFeatureMenuModel.HoverMenuSize.Medium;
		return HoverFeatureMenuModel.HoverMenuSize.Large;
	};

	const close = () => {
		const state = globalState();
		if (state.isContextMenuOpen) return;
		closeHoverFeatureMenu();
	};

	const isContextMenuOpen = () => globalState().isContextMenuOpen;

	return {
		isOpen: () => globalState().isOpen,
		context: () => globalState().context,
		footerActions,
		contextMenuActions,
		close: close,
		getMenuSize,
		isContextMenuOpen
	};
};

export const useHoverFeatureContext = () => {
	const methods = useCustomContext(HoverFeatureMenuModel.HoverMenuContext);
	return methods;
};

export const calculateActionCost = (action: HoverFeatureMenuModel.HoverFeatureAction) => {
	return action.associatedGlobalIds.reduce((acc, globalId) => {
		const price = getNodeBasePrice(globalId);
		if (price) {
			return acc + price;
		}
		return acc;
	}, 0);
};
